// News subject/message can reach the widget in three shapes: a plain value,
// the canonical { localized: { en: ... } } object, or a bare per-language map
// ({ en: ... }) stored by older API/MCP news writes. The server resolves the
// wrapped shapes before responding, but the bare map historically leaked
// through unresolved — and rendering it as a React child crashes the whole
// widget (Minified React error #31, ticket #143258). These resolvers accept
// any of the shapes so malformed data can never take the widget down.

const normalizeLangKey = (lang: string) => lang.toLowerCase().replace("_", "-");

const pickFromMap = (map: Record<string, any>, lang: string) => {
  const keys = Object.keys(map);
  if (keys.length === 0) {
    return undefined;
  }

  const wanted = normalizeLangKey(lang || "");
  const exact = keys.find((key) => normalizeLangKey(key) === wanted);
  if (exact !== undefined) {
    return map[exact];
  }

  const wantedBase = wanted.split("-")[0];
  const baseMatch = keys.find(
    (key) => normalizeLangKey(key).split("-")[0] === wantedBase,
  );
  if (baseMatch !== undefined) {
    return map[baseMatch];
  }

  const english = keys.find(
    (key) => normalizeLangKey(key).split("-")[0] === "en",
  );
  if (english !== undefined) {
    return map[english];
  }

  return map[keys[0]];
};

export const resolveLocalizedString = (value: any, lang: string): string => {
  if (typeof value === "string") {
    return value;
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return "";
  }

  const map =
    value.localized && typeof value.localized === "object"
      ? value.localized
      : value;
  const picked = pickFromMap(map, lang);
  return typeof picked === "string" ? picked : "";
};

// Same resolution for rich-text content (TipTap documents). A raw TipTap doc
// (has type/content) is already a renderable value and passes through.
export const resolveLocalizedContent = (value: any, lang: string): any => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return value;
  }
  if (value.localized && typeof value.localized === "object") {
    return pickFromMap(value.localized, lang);
  }
  if (value.type || value.content) {
    return value;
  }

  const entries = Object.values(value);
  const isLocaleMap =
    entries.length > 0 &&
    entries.every(
      (entry) => entry && typeof entry === "object" && !Array.isArray(entry),
    );
  if (!isLocaleMap) {
    return value;
  }
  return pickFromMap(value, lang);
};
